import './Page.scss';

import {
  ColDef,
  ColGroupDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  RowDragEndEvent,
  RowDragEnterEvent,
} from '@amzn/gft-ag-grid-enterprise';
import { GFTSearchInput } from '@amzn/gft-frontend-commons';
import AurigaLoader from '@components/Common/AurigaLoader';
import { KPI_DIMENSION_CONFIG_MAP, KPI_NAME_INDENT_SPACES } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useFootnotesContext } from '@context/FootnotesContext.tsx';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import AuditHistoryDialog from '@deckManagement/components/AuditHistory/AuditHistoryDialog';
import CommentsDrawer from '@deckManagement/components/Comments/CommentsDrawer';
import useCommentsDrawer from '@deckManagement/components/Comments/hooks/useCommentsDrawer';
import AsyncSaveButton from '@deckManagement/components/Common/AsyncSaveButton/AsyncSaveButton';
import { splitRawFormula } from '@deckManagement/components/Common/CalculatedKPIBuilder/utils/formulaHelpers';
import LockPageButton from '@deckManagement/components/Common/LockPageButton/LockPageButton';
import KpiNameCellRenderer from '@deckManagement/components/Page/CellRenderers/KpiNameCellRenderer';
import SegmentsCellRenderer from '@deckManagement/components/Page/CellRenderers/SegmentsCellRenderer';
import DisplayNameDialog from '@deckManagement/components/Page/DisplayNameDialog/DisplayNameDialog';
import useDisplayNameDialog from '@deckManagement/components/Page/DisplayNameDialog/useDisplayNameDialog';
import FootnotesModal from '@deckManagement/components/Page/Footnotes/FootnotesModal';
import FootnotesSection from '@deckManagement/components/Page/Footnotes/FootnotesSection';
import DimensionHeaderComponent from '@deckManagement/components/Page/HeaderComponents/DimensionHeaderComponent';
import ExpandableHeaderGroup from '@deckManagement/components/Page/HeaderComponents/ExpandableHeaderGroup';
import SectionGrid from '@deckManagement/components/Page/SectionGrid/SectionGrid';
import useGridReady from '@deckManagement/hooks/useGridReady';
import usePageGridManager, { type GridManager } from '@deckManagement/hooks/usePageGridManager';
import useDecklineGridStyling from '@hooks/useDecklineGridStyling';
import useEditDurationTracker from '@hooks/useEditDurationTracker';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DownloadIcon from '@mui/icons-material/Download';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { createId } from '@paralleldrive/cuid2';
import { CodeOrDescription, DeckColumn, KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { determineDecklineType } from '@utils/decklineHelpers';
import { downloadBlob, formatCellValue, processExcelExport } from '@utils/excelExportHelpers';
import { convertFootnoteNumberToSuperscript } from '@utils/footnoteHelpers';
import { getEffectiveDrivers } from '@utils/kpiDriverHelpers';
import { pageDecklineContextMenuOptions } from '@utils/pageHelpers';
import type { CustomHeaderProps } from 'ag-grid-react';
import { every, filter, forEach, includes, isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MenuOption } from 'types/Common.types';
import { EnhancedDeckline, NewDecklinePosition } from 'types/DecklinesContext.types';
import { ContextMenuEnums, DecklineRowData, PageGranularity, PageLayout } from 'types/Page.types';
import { KpiDimension, PageSidePanelDropDown } from 'types/PageSidePanel.types';
import useResizeObserver from 'use-resize-observer';

// The max age (in days) for a Comment item to be displayed.
const COMMENT_MAX_AGE_DAYS = 60;

// Sets the minimum number of characters needed for Search. Can adjust as needed.
const MINIMUM_SEARCH_TERM_LENGTH = 2;

// Pixel buffer so rendered Grid does not overlap even in the case of additions like System Alerts
const GRID_HEIGHT_BUFFER = 32;

/**
 * Formats a DimSum Node Description for display purposes (i.e.: removes "[Weight: 1]")
 * @param description
 */
const formatNodeDescription = (description: string) => {
  return description.replace(/\[Weight.*?]$/, '').trim();
};

/**
 * Page Component that serves the needs of rendering an AGGrid based on associated Template information
 */
const Page = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { toastNotification, featureFlags, renderAlerts } = useAurigaContext();
  const { currentDeckId } = useDeckContext();
  const {
    availablePageGrains,
    currentGranularityPage,
    currentCorePageName,
    pageDeckColumns,
    selectedGranularity,
    setSelectedGranularity,
    isLoading,
    segmentsDisplayMode,
    modifiedPageProperties,
    pageRowData,
    setPageRowData,
    pageLockStatus,
  } = usePageContext();
  const {
    selectedDeckline,
    pageDecklines,
    resolvedNodes,
    setSubtotalMap,
    setSubtotalRanges,
    isLoading: isDecklinesLoading,
    insertPageDeckline,
    deletePageDeckline,
    handleSave,
    isSaving,
    chooseDeckline,
    repositionDeckline,
    updateDecklineComments,
    updatePageDeckline,
  } = useDecklinesContext();

  const {
    isLoading: isFootnotesLoading,
    getFormattedFootnotesForExport,
    setDisplayFootnotesModal,
  } = useFootnotesContext();

  const { pendingCreations } = useKPILibraryContext();

  // Setup comments drawer for Decklines
  const commentsDrawer = useCommentsDrawer({
    key: currentGranularityPage?.sk,
    selectedItem: selectedDeckline,
    maxAgeInDays: COMMENT_MAX_AGE_DAYS,
    onSave: updateDecklineComments,
    getItemId: (deckline) => deckline.metadata.internalId,
    getItemLabel: (deckline) => deckline.metadata.actual_kpi_name ?? '',
  });

  // Display name override dialog
  const displayNameDialog = useDisplayNameDialog(selectedDeckline, updatePageDeckline);

  const { page_drivers: currentPageKPIDrivers } = modifiedPageProperties || {};

  // Edit duration tracking for RUM metrics
  const { recordSaveDuration } = useEditDurationTracker();

  // Persists current Page layout definition to local State
  const [pageLayout, setPageLayout] = useState<PageLayout | null>(null);

  // For tracking the full save lifecycle (pending creations + decklines + footnotes)
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);

  // For tracking row drag events
  const [rowDragStartIndex, setRowDragStartIndex] = useState<number | null>(null);

  // For tracking Search across the current Page
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchMatches, setSearchMatches] = useState<Set<string>>(new Set());

  // State for dimension column visibility
  const [isDimensionVisible, setIsDimensionVisible] = useState(false);
  const isDimensionVisibleRef = useRef(isDimensionVisible);
  isDimensionVisibleRef.current = isDimensionVisible;

  // Page Grid Api manager
  // Note: Use the gridManagerRef directly when needing to use Grid manager functions
  const gridManager = usePageGridManager();
  const gridManagerRef = useRef<GridManager | null>(null);
  gridManagerRef.current = gridManager;

  // Grid utilities for dimension header handling
  const gridApiRef = useRef(null);
  const { onGridReady: gridReadyHandler } = useGridReady(gridApiRef, {
    setIsDimensionVisible,
  });

  // Used for dynamically managing Footnotes Section width and margins
  const { ref: containerRef, width: footnotesContainerWidth = 1 } = useResizeObserver();
  const { ref: footnotesRef, height: footnotesSectionHeight = 1 } = useResizeObserver();

  // For calculating the max Grid height (from rendered Grid top position to Footnotes Section top position)
  const gridLayoutNodeRef = useRef<HTMLDivElement | null>(null);
  const [maxGridHeight, setMaxGridHeight] = useState<number | null>(null);

  /**
   * Calculates the maximum height a Grid can be, based on the current viewport and Footnotes Section height.
   */
  const calculateGridHeight = useCallback(() => {
    const gridLayoutElement = gridLayoutNodeRef.current;
    if (gridLayoutElement) {
      const gridTop = gridLayoutElement.getBoundingClientRect().top;
      setMaxGridHeight(window.innerHeight - gridTop - footnotesSectionHeight - GRID_HEIGHT_BUFFER);
    }
  }, [footnotesSectionHeight]);

  /**
   * Callback ref to track the Grid Layout container element and calculate its top position
   */
  const gridLayoutCallbackRef = useCallback(
    (gridLayoutElement: HTMLDivElement | null) => {
      // Pass so Footnotes Section useResizeObserver can track it (above)
      containerRef(gridLayoutElement);
      // Ensure our ref is in sync with container node
      gridLayoutNodeRef.current = gridLayoutElement;
      calculateGridHeight();
    },
    [containerRef, calculateGridHeight],
  );

  /**
   * Recalculates grid height on window resize and when parent element resizes
   * (e.g. User resizes browser, or system alerts appearing/disappearing above the grid).
   */
  useEffect(() => {
    calculateGridHeight();
    window.addEventListener('resize', calculateGridHeight);

    // Observe position changes (e.g. System Alerts appearing above the grid)
    const observer = new ResizeObserver(calculateGridHeight);
    const parent = gridLayoutNodeRef.current?.parentElement;
    if (parent) observer.observe(parent);

    return () => {
      window.removeEventListener('resize', calculateGridHeight);
      observer.disconnect();
    };
  }, [calculateGridHeight, renderAlerts]);

  // Deckline Grid Styling
  const decklineGridStyling = useDecklineGridStyling({
    pageDecklines,
    searchMatches,
    currentPageKPIDrivers: currentPageKPIDrivers || null,
  });

  // Individual display modes for each dimension
  const [dimensionDisplayModes, setDimensionDisplayModes] = useState<Record<KpiDimension, CodeOrDescription>>(
    Object.values(KpiDimension).reduce(
      (acc, dimension) => {
        acc[dimension] = segmentsDisplayMode;
        return acc;
      },
      {} as Record<KpiDimension, CodeOrDescription>,
    ),
  );
  useEffect(() => {
    // Performs a cleanup for all section grids
    return () => {
      // Unregister all grids when component unmounts
      forEach(Object.keys(pageLayout?.pageSections || {}), (sectionKey) => {
        gridManagerRef.current?.unregisterPageGrid(sectionKey);
      });
    };
  }, [pageLayout]);

  /**
   * Parses through DeckColumn Data received from the Api, for a given Page grain, and formats data
   * to populate the Page Column layout
   * @param columnData - The raw Page DeckColumn data to parse and use for layout
   */
  const generatePageLayout = (columnData: DeckColumn[]): PageLayout | null => {
    if (!columnData || columnData.length === 0) return null; // As no columns exist

    // Group columns by DeckColumns page_section to determine relevant section headers
    const sectionGroups: Record<string, DeckColumn[]> = {};
    forEach(columnData, (column) => {
      const sectionGroup = column?.page_section || 'default';

      if (!sectionGroups[sectionGroup]) {
        sectionGroups[sectionGroup] = [];
      }
      sectionGroups[sectionGroup].push(column);
    });

    // Define page layout response structure
    const currPageLayout: PageLayout = { pageSections: {} };

    // Create each section from its column section group
    forEach(Object.keys(sectionGroups), (sectionKey, index) => {
      const sectionColumns = sectionGroups[sectionKey];
      const sectionGroupKey = `sectionGroup_${index + 1}`; // Results in sectionGroup_1, sectionGroup_2, etc.

      // Group by 'block_id'
      const blocks: Record<string, DeckColumn[]> = {};
      forEach(sectionColumns, (column: DeckColumn) => {
        if (column?.block_id) {
          const blockKey = `block_${column.block_id}`;
          if (!blocks[blockKey]) blocks[blockKey] = [];
          blocks[blockKey].push(column);
        }
      });

      // Sort blocks by keys and create column groups for the layout
      const sectionBlocks: Record<string, Record<string, DeckColumn[]>> = {};
      Object.keys(blocks)
        .sort((keyA, keyB) => parseInt(keyA.replace('block_', ''), 10) - parseInt(keyB.replace('block_', ''), 10))
        .forEach((blockKey) => {
          // Sort block associated columns by 'column_no'
          const blockColumns = blocks[blockKey].sort(
            (columnA, columnB) => (columnA.column_no || 0) - (columnB.column_no || 0),
          );

          // Group by 'column_no' to handle multiple periods per column (SubColumns)
          const columnGroups: Record<string, DeckColumn[]> = {};
          forEach(blockColumns, (column: DeckColumn) => {
            if (column?.column_no) {
              const columnKey = `column_${column.column_no}`;
              if (!columnGroups[columnKey]) columnGroups[columnKey] = [];
              columnGroups[columnKey].push(column);
            }
          });

          sectionBlocks[blockKey] = columnGroups;
        });

      currPageLayout.pageSections[sectionGroupKey] = {
        blocks: sectionBlocks,
      };
    });

    // Final generated Page Layout
    return currPageLayout;
  };

  // Update the PageLayout in State whenever Page DeckColumns is updated
  useEffect(() => {
    setPageLayout(generatePageLayout(pageDeckColumns || []));
  }, [pageDeckColumns]);

  /**
   * Group Decklines by their mapped page section
   */
  const groupDecklinesBySection = (decklines: DecklineRowData[]) => {
    const decklinesBySection: Record<string, DecklineRowData[]> = {};

    forEach(decklines, (deckline) => {
      const pageSection = `sectionGroup_${deckline.rawDecklineData.page_section || '1'}`;

      if (!decklinesBySection[pageSection]) {
        decklinesBySection[pageSection] = [];
      }
      decklinesBySection[pageSection].push(deckline);
    });

    return decklinesBySection;
  };

  /**
   * Creates AGGrid Columns based on generated PageLayout
   * @param pageLayoutData - The current PageLayout to generate the grid Columns from
   */
  const createGridColumnsFromLayout = useCallback(
    (pageLayoutData: PageLayout): ColGroupDef[] => {
      if (!pageLayoutData || isEmpty(pageLayoutData)) return []; // As sufficient Page Layout data does not exist

      const columnDefs: ColGroupDef[] = [];

      // Iterate through each section to generate column data
      forEach(Object.keys(pageLayoutData.pageSections), (pageSectionKey) => {
        const section = pageLayoutData.pageSections[pageSectionKey];

        // Create section-level column groups
        const sectionColumnDefs = Object.keys(section.blocks)
          .map((blockKey) => {
            const columnGroups = section.blocks[blockKey];
            if (columnGroups && Object.keys(columnGroups).length > 0) {
              // Header to display in the block (would be the same across all current block columns)
              const firstColumnGroup = Object.values(columnGroups)[0];
              const blockHeader = firstColumnGroup[0]?.block_header || '';

              /**
               * Collect children (as ColDef) to be applied to returned ColGroupDef for AGGrid rendering
               * Note: Expectation is that generatePageLayout function already sorted the columnGroups by 'column_no'
               *
               * Follows the below format:
               *
               * ColGroupDef (Block Header: "Amount")
               *  ---> ColGroupDef (Actuals)
               *  -------> ColDef (ScenarioPeriod: 'Sep-25') example
               *  ---> ColGroupDef (OP2)
               *  -------> ColDef (ScenarioPeriod: 'Q4') example
               *  ---> etc.
               */
              const children: ColDef[] = Object.keys(columnGroups).flatMap((columnKey) => {
                const subColumns = columnGroups[columnKey];

                // Filter subColumns to the record matching the selected granularity's time grain.
                // Some pages may contain records for multiple grains (e.g., MTD + QTD); This will only render the relevant and active one.
                const timeGrainMapping: Record<string, string[]> = {
                  MTD: ['MONTH', 'MTD'],
                  QTD: ['QTR', 'QTD'],
                  YTD: ['FY', 'YTD'],
                };
                const validGrains = timeGrainMapping[selectedGranularity || ''] || [];
                // Falls back to subColumns[0] for unmapped grains where all records may share the same time_grain
                const activeSubColumn =
                  subColumns.find((col) => validGrains.includes(col.time_grain || '')) || subColumns[0];

                // Header to display scenarioName (would be the same across all current subColumns)
                const scenarioName = activeSubColumn.scenario_display_name || '';

                /**
                 * Create ColGroupDef (ScenarioName) items with child ColDef (ScenarioPeriod) Time Series
                 */
                return {
                  headerName: scenarioName,
                  headerClass: 'scenario-header',
                  children:
                    // Create one display column per column_no using the active grain's subColumn
                    [activeSubColumn].map((subItem) => {
                      const scenarioPeriod = subItem?.scenario_period;
                      return {
                        field: `${subItem?.column_name ? subItem?.column_name.toLowerCase() : 'MissingSubColName'}_${selectedGranularity?.toLowerCase()}`,
                        headerName: scenarioPeriod,
                        initialFlex: 1,
                        resizable: false, // Disable resize
                        hide: isNil(scenarioPeriod), // Hides Column if Scenario Period is Nil or Undefined
                        valueFormatter: (params) =>
                          // Format numeric cell values for grid display
                          // TODO: Only considers row level formatting based on deckline schema
                          //  Will need revisiting on other level of formatting is supported (e.g. Columns, Blocks, etc.)
                          formatCellValue(params.value, params.data?.rawDecklineData?.format_cell_default),
                      } as ColDef;
                    }),
                } as ColGroupDef;
              });

              // Returns BlockHeader ColGroupDef with children of ScenarioName and Time Series ColGroupDef defined above
              return {
                headerName: blockHeader,
                headerClass: 'block-header',
                children,
              } as ColGroupDef;
            }
            return null;
          })
          .filter(Boolean) as ColGroupDef[];

        columnDefs.push(...sectionColumnDefs);
      });

      return columnDefs;
    },
    [selectedGranularity],
  );

  /**
   * Create columns for one or more sections in the Page view
   */
  const createColumnsForSection = useCallback(
    (pageLayoutData: PageLayout, pageSectionKey: string): ColGroupDef[] => {
      if (!pageLayoutData?.pageSections[pageSectionKey]) return [];

      const singleSectionLayout: PageLayout = {
        pageSections: { [pageSectionKey]: pageLayoutData.pageSections[pageSectionKey] },
      };

      return createGridColumnsFromLayout(singleSectionLayout);
    },
    [createGridColumnsFromLayout],
  );

  /**
   * Utility function to generate the RevGP Cell Value for a given Deckline Row, based on the RevGP Display Mode
   * @param decklineDrivers - The Deckline Drivers to parse and return the RevGP Cell Value for
   */
  const generateCellValue = useCallback(
    (dimension: KpiDimension, decklineDrivers?: KPIDrivers | null) => {
      const valueParts = [];

      if (decklineDrivers) {
        const config = KPI_DIMENSION_CONFIG_MAP[dimension];
        if (!config?.decklineDrivers) return '';

        const hierarchyValue = decklineDrivers[config.decklineDrivers.hierarchyAccessor as keyof KPIDrivers];
        const nameValue = decklineDrivers[config.decklineDrivers.valueAccessor as keyof KPIDrivers];

        if (!hierarchyValue || !nameValue) return nameValue || '';

        const nodeId = `${hierarchyValue}-${nameValue}`;
        const resolvedNode = resolvedNodes?.[nodeId];

        if (resolvedNode) {
          const displayMode = dimensionDisplayModes[dimension];
          if ([CodeOrDescription.CODE, CodeOrDescription.BOTH].includes(displayMode)) {
            valueParts.push(resolvedNode.attributes.name);
          }
          if ([CodeOrDescription.DESCRIPTION, CodeOrDescription.BOTH].includes(displayMode)) {
            valueParts.push(formatNodeDescription(resolvedNode.attributes.description));
          }
        } else if (nameValue) {
          valueParts.push(nameValue);
        }
      }
      return valueParts.join(' - ');
    },
    [resolvedNodes, dimensionDisplayModes],
  );

  /**
   * Build subtotal map and ranges from decklines data
   */
  const buildSubtotalMaps = useCallback(
    (rowData: DecklineRowData[]) => {
      const subtotalMap: Map<string, DecklineRowData[]> = new Map();
      const subtotalRanges: Map<string, { min: number; max: number }> = new Map();

      // Group by subtotal_parent (include all rows for highlighting)
      rowData.forEach((row) => {
        const subtotalParent = row.rawDecklineData?.subtotal_parent;
        if (subtotalParent) {
          if (subtotalMap.has(subtotalParent)) {
            subtotalMap.get(subtotalParent)!.push(row);
          } else {
            subtotalMap.set(subtotalParent, [row]);
          }
        }
      });

      // Calculate subtotal ranges (only use rows with valid row numbers)
      Array.from(subtotalMap.entries()).forEach(([key, childRows]) => {
        const rowNumbers = childRows.map((row) => Number(row.rowNumber)).filter((n) => !Number.isNaN(n));
        if (rowNumbers.length > 0) {
          subtotalRanges.set(key, {
            min: Math.min(...rowNumbers),
            max: Math.max(...rowNumbers),
          });
        }
      });

      setSubtotalMap(subtotalMap);
      setSubtotalRanges(subtotalRanges);
    },
    [setSubtotalMap, setSubtotalRanges],
  );

  /**
   * Parses through Deckline Row Data received from the Api, for a given Page ID, and formats data
   * to populate the Row items in AGGrid
   * @param decklinesData - The raw Page Deckline data to parse and format
   */
  const createDecklineRowData = useCallback(
    (decklinesData: EnhancedDeckline[]): DecklineRowData[] => {
      if (!decklinesData || decklinesData.length === 0) return []; // As no rows exist

      // Preview Mode (Dev-Admin gated): Single check of localStorage access on whether to generate mock data on the page view
      const isPreviewMode = localStorage.getItem('auriga-preview-mode') === 'true' && !!pageDeckColumns?.length;

      // Display number to apply to all Rows that are not BLANKLINE rows
      let displayRowNumber = 0;
      // Format raw Decklines data to useable DecklineRowData
      const rowData = decklinesData.map((rawDeckline, idx) => {
        const decklineType = determineDecklineType(rawDeckline);
        const isBlankLine = decklineType === PageSidePanelDropDown.BLANK_LINE;
        const isCalculatedKpi = decklineType === PageSidePanelDropDown.CALCULATED_KPI;
        const isAdjustment = decklineType === PageSidePanelDropDown.ADJUSTMENT;
        const isCalculatedAdjustment = decklineType === PageSidePanelDropDown.CALCULATED_ADJUSTMENT;

        const kpiDecklineDrivers = getEffectiveDrivers(rawDeckline?.deckline_drivers, currentPageKPIDrivers);
        if (!isBlankLine) displayRowNumber += 1;

        // For calculated KPIs with a formula, parse it to get actual dimension values
        const parsedFormula =
          isCalculatedKpi && rawDeckline?.kpi_formula && !isEmpty(rawDeckline.kpi_formula)
            ? splitRawFormula(rawDeckline.kpi_formula, currentPageKPIDrivers ?? undefined)
            : null;

        // Generate all dimension values
        const dimensionValues = Object.values(KpiDimension).reduce(
          (acc, dimension) => {
            if (isAdjustment) {
              acc[dimension] =
                dimension === KpiDimension.REVGP ? '(Adjustment)' : generateCellValue(dimension, kpiDecklineDrivers);
            } else if (isCalculatedAdjustment) {
              acc[dimension] =
                dimension === KpiDimension.REVGP
                  ? '(Calculated Adjustment)'
                  : generateCellValue(dimension, kpiDecklineDrivers);
            } else if (parsedFormula) {
              const valueAccessor = KPI_DIMENSION_CONFIG_MAP[dimension]?.decklineDrivers?.valueAccessor;
              const entry = valueAccessor ? parsedFormula[valueAccessor] : undefined;

              if (entry?.isFormula) {
                acc[dimension] = '(Calculated)';
              } else if (entry?.value) {
                acc[dimension] = entry.value;
              } else {
                acc[dimension] = generateCellValue(dimension, kpiDecklineDrivers);
              }
            } else if (isCalculatedKpi) {
              // Calculated KPI without a parseable formula — show (Calculated) for RevGP, fallback for others
              acc[dimension] =
                dimension === KpiDimension.REVGP ? '(Calculated)' : generateCellValue(dimension, kpiDecklineDrivers);
            } else {
              acc[dimension] = generateCellValue(dimension, kpiDecklineDrivers);
            }
            return acc;
          },
          {} as Record<KpiDimension, string | null>,
        );
        // Preview Mode (Dev-Admin gated): Seed deterministic numeric values into data column fields for visual validation before Excel export
        const previewValues: Record<string, number> = {};
        if (!isBlankLine && isPreviewMode) {
          const format = rawDeckline?.format_cell_default;
          const isPercent = format === 'PERCENT' || format === 'PERCENT_1' || format === 'PERCENT_2';
          const isBps = format === 'BPS';

          // Deterministic seed based on row index — values are stable across re-renders
          pageDeckColumns.forEach((col, colIdx) => {
            if (col?.column_name) {
              const field = `${col.column_name.toLowerCase()}_${selectedGranularity?.toLowerCase()}`;
              const seed = ((idx + 1) * 7 + (colIdx + 1) * 13) % 100; // 0-99 deterministic per cell
              if (isPercent) {
                previewValues[field] = +((seed / 100) * 0.4 - 0.1).toFixed(4); // -0.10 to 0.30 (decimal form)
              } else if (isBps) {
                previewValues[field] = Math.round((seed / 100) * 500 - 100);
              } else {
                previewValues[field] = Math.round((seed / 100) * 50000 - 5000);
              }
            }
          });
        }

        const currentDecklineRowData = {
          id: idx + 1,
          rowNumber: isBlankLine ? '' : displayRowNumber, // Empty for blank lines
          kpi_name: rawDeckline?.kpi_name,
          // Store the original raw Deckline data for reference
          rawDecklineData: rawDeckline,
          ...dimensionValues,
          ...previewValues,
        } as DecklineRowData;

        return currentDecklineRowData;
      });

      // Build subtotal maps using the new function
      buildSubtotalMaps(rowData);
      return rowData;
    },
    [buildSubtotalMaps, generateCellValue, currentPageKPIDrivers, pageDeckColumns, selectedGranularity],
  );

  // Determines if the Page is still loading — includes all data dependencies and readiness checks to prevent loader flicker
  const isDataLoading = isLoading || isDecklinesLoading || isFootnotesLoading || isNil(currentGranularityPage);
  useRecordLoadComplete(!isDataLoading && !isNil(currentGranularityPage));

  // Forces Page grids to refresh when pageRowData changes, or when search matches change
  useEffect(() => {
    const dataProcessTimeouts: ReturnType<typeof setTimeout>[] = [];
    if (!isSaving && !gridManagerRef?.current?.isDragging?.current) {
      gridManagerRef.current?.executeOnAll((gridApi) => {
        // Add a small delay to ensure data is fully processed
        // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout -- tracked in dataProcessTimeouts array, cleared in cleanup
        const dataProcessTimeout = setTimeout(() => {
          gridApi?.refreshCells({ force: true });
          gridApi?.redrawRows();
        }, 0); // Setting 0 here pushes the refresh to the next tick of the event cycle
        dataProcessTimeouts.push(dataProcessTimeout);
      });
    }

    return () => {
      for (const timeoutId of dataProcessTimeouts) clearTimeout(timeoutId);
    };
  }, [searchMatches, pageRowData, isSaving]);

  // Refresh headers across all Section grids when segmentsDisplayMode changes
  useEffect(() => {
    gridManagerRef.current?.executeOnAll((gridApi) => {
      gridApi.refreshHeader();
    });
  }, [dimensionDisplayModes]);

  // Update the Deckline row data whenever Page Decklines is updated, and data has loaded
  useEffect(() => {
    if (!isDataLoading && pageDecklines) {
      const newRowData = createDecklineRowData(pageDecklines);
      setPageRowData(newRowData);
    }
  }, [isDataLoading, createDecklineRowData, pageDecklines, resolvedNodes, setPageRowData]);

  /**
   * Monetary unit of measure for financial documents.
   * This is the current standard for US-based financial reporting.
   *
   * Note*: This hard-coded logic should be revisited if the case arises to display non-US currency (e.g. Euro),
   * or to display money on a different scale (e.g. thousands, trillions, etc.)
   */
  const defaultMonetaryUnit = '$ in MM';

  /**
   * Generate the expandable Dimension Headers for the Grid, based on the current KPI Drivers
   *  - Filters out the Reporting Currency dimension
   *  - Adds a custom header component for all dimensions except COST_TYPE
   */
  const generateDimensionHeaders = useCallback((): ColDef<DecklineRowData>[] => {
    return Object.values(KPI_DIMENSION_CONFIG_MAP)
      .filter((config) => config.key !== KpiDimension.REPORTING_CURRENCY)
      .map((config): ColDef<DecklineRowData> => ({
        colId: `dimension-${config.key}`, // Add unique column ID
        headerName: config.label,
        field: config.key,
        ...(currentPageKPIDrivers?.[config.key as keyof KPIDrivers] !== null && { columnGroupShow: 'open' }),
        hide: false,
        flex: 1,
        minWidth: 150,
        maxWidth: 225,
        headerComponent:
          config.key === KpiDimension.COST_TYPE
            ? undefined
            : (props: CustomHeaderProps) => (
                <DimensionHeaderComponent
                  {...props}
                  dimension={config.key as KpiDimension}
                  dimensionDisplayModes={dimensionDisplayModes}
                  setDimensionDisplayModes={setDimensionDisplayModes}
                  gridManagerRef={gridManagerRef}
                />
              ),
        cellRenderer: SegmentsCellRenderer,
        cellRendererParams: {
          inheritedValueClassName: 'page-driver-inherited-value',
          valueAccessor: config.decklineDrivers?.valueAccessor,
          hierarchyAccessor: config.decklineDrivers?.hierarchyAccessor,
        },
      }));
  }, [currentPageKPIDrivers, dimensionDisplayModes]);

  /**
   * Memoized Core Grid Column Definitions using PageGridColumns and other Api retrieved data
   */
  const coreGridColumnDefs: (ColGroupDef<DecklineRowData> | ColDef<DecklineRowData>)[] = useMemo(
    () => [
      {
        headerName: pageDeckColumns?.[0]?.report_header
          ? `${pageDeckColumns[0].report_header} (${selectedGranularity})\n${defaultMonetaryUnit}`
          : 'Missing Report Header',
        children: [
          // Static Column definition (with empty header) to reserve headerRow allocation for Scenario Period Time Series
          {
            headerName: '',
            pinned: 'left',
            children: [
              {
                field: 'rowNumber',
                rowDrag: (params) => !params.data?.rawDecklineData?.subtotal_formula, // Disable drag for subtotal rows
                headerName: '', // Intentional do not label header for Row
                autoHeight: true, // Override defaults to allow Auto height for Row Number column
                minWidth: 80, // Adjusting to default of 80 to avoid excessive resizing
                maxWidth: 100, // Maximum width to prevent over-expansion
                pinned: 'left', // To keep in view on horizontal scroll
                resizable: false,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                suppressSizeToFit: true,
                valueFormatter: (params) => (params.value === '' ? '' : params.value), // Handles the need for skipping row numbers (e.g. BLANKLINES)
              },
              {
                headerName: '',
                initialFlex: 1,
                field: 'kpi_name', // TODO: Handle setting of value based on CodeOrDescription. Default set above, is CODE.
                pinned: 'left',
                resizable: false,
                flex: 1,
                // [2026-06-03] Per UXD Request: Increased to 250
                minWidth: 250,
                // [2026-05-28] Per UXD Request: Increased to 350
                maxWidth: 350, // Allow greater width than GridOptions default
                wrapText: true, // Override defaults to allow Wrapped Text
                autoHeight: true, // Override defaults to allow Auto Height for Wrapped Text
                cellRenderer: KpiNameCellRenderer,
                cellClass: decklineGridStyling.cellClassHandler((params, defaultColDefClasses) => {
                  const newClasses = [...defaultColDefClasses, 'ag-cell-wrapper-full-width'];
                  // Checks for deckline comments:
                  if (params.data?.rawDecklineData?.comments?.length) {
                    newClasses.push('has-comments');
                  }
                  return newClasses;
                }),
              },
            ],
          },
        ],
      },
      {
        headerName: isDimensionVisible ? `Hide Dimensions` : `Show Dimensions`,
        headerGroupComponent: ExpandableHeaderGroup,
        children: [
          {
            headerName: '', // Intentional empty header to support placement of Hide/Show Dimension label and chevron
            openByDefault: false,
            groupId: 'expandableColumn', // For state persistence
            children: generateDimensionHeaders(),
          },
        ],
      },
    ],
    [pageDeckColumns, selectedGranularity, decklineGridStyling, isDimensionVisible, generateDimensionHeaders],
  );

  /**
   * Handler for when a row is dragged and dropped within the AGGrid component.
   * @param event - The RowDragEndEvent from AGGrid
   */
  const handleRowDragStart = useCallback((event: RowDragEnterEvent<DecklineRowData>) => {
    setRowDragStartIndex(event.node.sourceRowIndex);
    gridManagerRef.current?.setDragging(true);
  }, []);

  /**
   * Handler for when a row is dragged and dropped within the AGGrid component.
   * @param event - The RowDragEndEvent from AGGrid
   * @param sectionRowData - The current row data for the section
   */
  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent<DecklineRowData>, sectionRowData: DecklineRowData[]) => {
      if (isNil(rowDragStartIndex) || isNil(event?.overIndex) || rowDragStartIndex === event.overIndex) {
        gridManagerRef.current?.setDragging(false);
        return;
      }

      const { overIndex } = event;

      // NOTE: The correct afterIndex depends on the direction of the drag/drop event.
      // If we're moving a deckline upwards in the list, we need to subtract one from the `overIndex` from the event.
      const afterIndex = rowDragStartIndex > overIndex ? overIndex - 1 : overIndex;

      const { rawDecklineData: targetDecklineData } = sectionRowData[rowDragStartIndex];
      const targetId = targetDecklineData.metadata.internalId;

      // NOTE: A `null` value here translates to moving the deckline to the top of the list.
      const afterId = afterIndex >= 0 ? sectionRowData[afterIndex].rawDecklineData.metadata.internalId : null;

      // Execute changes when drag is completed
      setTimeout(() => {
        repositionDeckline(targetId, afterId);
        setRowDragStartIndex(null);
        // Select the dragged deckline so that the sidepanel populates.
        chooseDeckline(targetDecklineData);
        gridManagerRef.current?.setDragging(false);
      }, 0);
    },
    [chooseDeckline, repositionDeckline, rowDragStartIndex],
  );

  /**
   * Creates Grid Options for rendering the AGGrid, and used memoized coreColumnDefs defined previously
   */
  const createGridOptions = useCallback(
    (sectionColumnDefs: ColGroupDef[], sectionRowData: DecklineRowData[]): GridOptions => ({
      gridId: createId(), // Provide a unique grid ID for each grid we create
      columnDefs: [...coreGridColumnDefs, ...sectionColumnDefs], // Sets the memoized Column Definitions to the Grid
      defaultColDef: {
        minWidth: 100, // Increase as needed to prevent single word breaks
        maxWidth: 125, // Force wrapping for very long headers
        resizable: true,
        wrapHeaderText: true, // Keep text wrapping
        wrapText: false, // Disable text wrapping for all cell content by default
        autoHeight: false, // Disable Auto adjust row height for wrapped cell content by default
        sortable: false,
        cellClass: decklineGridStyling.cellClassHandler(),
      },
      excelStyles: decklineGridStyling.excelStyles,
      animateRows: true, // Allow row animation
      enableBrowserTooltips: false,
      groupHeaderHeight: 60,
      headerHeight: 60,
      rowBuffer: 5, // Reduce row buffer from default of 10, to aid performance
      rowData: sectionRowData, // Sets State Decklines data for the rows
      rowDragManaged: false, // Disabled to allow self-management of row reorder
      rowDragText: (params) => {
        // Show KPI Name column data when dragging
        return params.rowNode?.data?.rawDecklineData?.kpi_name;
      },
      suppressMenuHide: true,
      suppressColumnVirtualisation: false, // Default, but explicitly ensuring column virtualization is allowed
      suppressRowVirtualisation: false, // Default, but explicitly ensuring row virtualization is allowed
      suppressMoveWhenRowDragging: true,
      getRowId: (params: GetRowIdParams) => String(params.data.id), // ID set in createDecklineRowData function
      getRowClass: decklineGridStyling.rowClassHandler,
      onRowDragEnter: handleRowDragStart,
      onRowDragEnd: (event) => handleRowDragEnd(event, sectionRowData),
      onGridReady: (params) => {
        gridReadyHandler(params);

        // Auto-size expandable columns based on content
        setTimeout(() => {
          const expandableCols = params.api.getColumns()?.filter((col) => col.getColId().startsWith('dimension-'));
          if (expandableCols?.length) {
            params.api.autoSizeColumns(expandableCols.map((col) => col.getColId()));
          }
        }, 300);
      },
      onCellFocused: (event) => {
        // Ignore event if `rowIndex` is nil or we're in the process of row dragging (`rowDragStartIndex` is populated, or isDragging for a grid is true)
        if (isNil(event.rowIndex) || !isNil(rowDragStartIndex) || gridManagerRef.current?.isDragging?.current) {
          return;
        }

        // NOTE: Moved `chooseDeckline` out of PageSidePanel and into this AGGRid event:
        // 1. When a user right-clicks for our context menu, this ensures the row below the cursor is selected.
        // 2. Arrow key navigation between rows now works with updating the Side Panel.
        if (
          selectedDeckline?.metadata?.internalId !== sectionRowData[event.rowIndex]?.rawDecklineData.metadata.internalId
        ) {
          chooseDeckline(sectionRowData[event.rowIndex].rawDecklineData);
        }
      },
      onColumnGroupOpened: (event) => {
        if (event.columnGroup?.getGroupId() === 'expandableColumn') {
          const originGridId = event.api.getGridId();
          const isExpanded = event.columnGroup.isExpanded();
          setIsDimensionVisible(isExpanded);
          // Propagate the group expanded state across all section grids.
          gridManagerRef.current?.executeOnAll((api) => {
            if (api.getGridId() !== originGridId) {
              api.setColumnGroupState([{ groupId: 'expandableColumn', open: isExpanded }]);
            }
          });
        }
      },
    }),
    [
      coreGridColumnDefs,
      decklineGridStyling,
      handleRowDragStart,
      handleRowDragEnd,
      gridReadyHandler,
      rowDragStartIndex,
      selectedDeckline?.metadata?.internalId,
      chooseDeckline,
    ],
  );

  /**
   * Handle search across Decklines for matches based on User Search term
   * Searches only across KPI ID and RevGP for a match.
   * @param term - The text to compare against targeted fields.
   */
  const handleSearch = useCallback(
    (term: string) => {
      if (term.length >= MINIMUM_SEARCH_TERM_LENGTH) {
        setIsSearching(true);

        // Match is based off lowercase string comparisons
        const matches = filter(pageRowData, (deckline) => {
          return (
            includes(deckline?.kpi_name?.toLowerCase(), term.toLowerCase()) ||
            includes(deckline?.revgp?.toLowerCase(), term.toLowerCase())
          );
        });

        if (!isEmpty(matches)) {
          const matchInternalIds = matches.map((match) => match.rawDecklineData.metadata.internalId);
          setSearchMatches(new Set(matchInternalIds));
        } else {
          // Clear State when no matches exist
          setSearchMatches(new Set());
        }

        // End search
        setIsSearching(false);
      }
    },
    [pageRowData],
  );

  /**
   * Handles search term input changes - called immediately on each keystroke
   * @param value - The changing search text to be updated in State when it meets minimum length requirements
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);

    if (value.length < MINIMUM_SEARCH_TERM_LENGTH) {
      setSearchMatches(new Set());
      setIsSearching(false);
    }
  }, []);

  /**
   * Resets the Search state as needed
   */
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchMatches(new Set());
    setIsSearching(false);
  }, []);

  /**
   * Handle saving Page View changes.
   * Resolves pending KPI/Variant creations first (since decklines reference their IDs),
   * then saves footnotes and decklines.
   */
  const handleSaveChanges = useCallback(async () => {
    setIsSaveInProgress(true);
    const { success, error } = await pendingCreations.resolvePendingCreations();
    if (!success) {
      setIsSaveInProgress(false);
      toastNotification({
        visible: true,
        message: `Failed to save new KPIs: ${error?.message ?? 'Unknown error'}`,
        type: 'error',
      });
      return;
    }

    // Success Handler for Save Action.
    const successHandler = () => {
      setIsSaveInProgress(false);
      recordSaveDuration({
        componentId: currentGranularityPage?.pk,
        component: currentCorePageName ?? undefined,
      });
      toastNotification({
        visible: true,
        message: 'Page and Deckline changes saved successfully.',
        type: 'success',
      });
    };

    // Error Handler for Save Action.
    // NOTE: DecklineContext handles most logic internally - this handler only adds component-specific requirements.
    const errorHandler = () => {
      setIsSaveInProgress(false);
    };

    commentsDrawer?.onClose();
    handleSave(successHandler, errorHandler);
  }, [
    pendingCreations,
    commentsDrawer,
    handleSave,
    toastNotification,
    recordSaveDuration,
    currentGranularityPage?.pk,
    currentCorePageName,
  ]);

  // To determine the right click menu options and actions for when no rows are shown
  // True if no rows yet exist, or if all existing rows are deleted
  const isFirstRowAdd = useMemo(
    () => pageDecklines.length === 0 || every(pageDecklines, (deckline) => deckline.metadata.isDeleted),
    [pageDecklines],
  );

  /**
   * Handles context menu actions for the selected row
   * @param actionValue - The context menu action value (e.g., 'Insert Row Below', 'Insert Row Above')
   */
  const contextMenuHandler = useCallback(
    (actionValue: string) => {
      if (selectedDeckline) {
        // Uses internal ID in metadata for tracking lookup reference
        const targetDecklineId = selectedDeckline.metadata.internalId;

        switch (actionValue) {
          case ContextMenuEnums.INSERT_ROW_BELOW:
            insertPageDeckline(targetDecklineId, NewDecklinePosition.BELOW);
            break;
          case ContextMenuEnums.INSERT_ROW_ABOVE:
            insertPageDeckline(targetDecklineId, NewDecklinePosition.ABOVE);
            break;
          case ContextMenuEnums.FOOTNOTES:
            setDisplayFootnotesModal(true);
            break;
          case ContextMenuEnums.DECKLINE_HISTORY:
            // Opens Deckline-specific Audit History dialog via search params
            setSearchParams({ history: 'deckline', decklineId: selectedDeckline.deckline_id! });
            break;
          case ContextMenuEnums.COMMENT:
            commentsDrawer.openDrawer();
            break;
          case ContextMenuEnums.DISPLAY_NAME_OVERRIDE:
            displayNameDialog.openDialog();
            break;
          case ContextMenuEnums.DELETE_DECKLINE:
            deletePageDeckline(targetDecklineId);
            break;
          case ContextMenuEnums.DEBUG_OUTPUT_DECKLINE:
            console.debug('[DEBUG] Deckline:', selectedDeckline);
            break;
          default:
            break;
        }
      } else if (isFirstRowAdd && actionValue === ContextMenuEnums.INSERT_ROW) {
        // Handle no rows exist on new page creation
        insertPageDeckline();
      }
    },
    [
      selectedDeckline,
      isFirstRowAdd,
      insertPageDeckline,
      setDisplayFootnotesModal,
      setSearchParams,
      deletePageDeckline,
      commentsDrawer,
      displayNameDialog,
    ],
  );

  /**
   * For tracking whether the Page in View is a single or multi-section Page.
   */
  const isMultiSectionPage = useMemo(
    () => (pageLayout?.pageSections ? Object.keys(pageLayout.pageSections).length > 1 : false),
    [pageLayout],
  );

  // Memoized context menu options for the right click context menu
  const contextMenuOptions = useMemo(
    (): MenuOption<ContextMenuEnums>[] =>
      pageDecklineContextMenuOptions(isFirstRowAdd, selectedDeckline, pageLockStatus.isEditingDisabled),
    [isFirstRowAdd, selectedDeckline, pageLockStatus.isEditingDisabled],
  );

  /**
   * Renders the AGGrid for a section with individual column group definitions and section specific page row lines
   * @param pageSectionKey - The key of the page section
   * @param sectionRowData - The row data for the section
   */
  const renderSectionGrid = useCallback(
    (pageSectionKey: string, sectionRowData: DecklineRowData[]) => {
      // Validate data belongs to current Page being viewed (allows for undefined with initial load)
      const dataPageName = sectionRowData[0]?.rawDecklineData?.page_name;
      const isCorrectPageData = isNil(dataPageName) || dataPageName === currentCorePageName;

      if (!isLoading && !isDecklinesLoading && pageDeckColumns && gridManagerRef.current && isCorrectPageData) {
        // Create section column group definitions, and final grid options to apply
        const columnGroupDefs = pageSectionKey && pageLayout ? createColumnsForSection(pageLayout, pageSectionKey) : [];
        const gridOptions = createGridOptions(columnGroupDefs, sectionRowData);

        return (
          <SectionGrid
            key={`${currentCorePageName}-${pageSectionKey}`}
            sectionLookupKey={pageSectionKey}
            sectionRowData={sectionRowData}
            sectionGridOptions={gridOptions}
            contextMenuOptions={contextMenuOptions}
            contextMenuHandler={contextMenuHandler}
            gridManager={gridManagerRef.current}
            isMultiSectionPage={isMultiSectionPage}
          />
        );
      }
      return null;
    },
    [
      isLoading,
      isDecklinesLoading,
      pageDeckColumns,
      pageLayout,
      currentCorePageName,
      createColumnsForSection,
      createGridOptions,
      contextMenuOptions,
      contextMenuHandler,
      isMultiSectionPage,
    ],
  );

  /**
   * Renders Page AG Grids based on available Page Row Data, collected by sections
   * @returns The rendered Page AG Grids
   */
  const renderPageGrids = useCallback(() => {
    if (!pageLayout || !pageRowData) return null;
    // Get section relevant decklines
    const decklinesBySection = groupDecklinesBySection(pageRowData);

    return (
      <>
        {Object.keys(pageLayout.pageSections).map((pageSectionKey) => {
          const sectionDecklines = decklinesBySection[pageSectionKey] || [];

          return (
            <Box key={`${currentCorePageName}-${pageSectionKey}`} className='section-grid-wrapper'>
              {renderSectionGrid(pageSectionKey, sectionDecklines)}
            </Box>
          );
        })}
      </>
    );
  }, [currentCorePageName, pageLayout, pageRowData, renderSectionGrid]);

  /**
   * Returns the number of columns in each block, used for Excel Export spacer column insertion.
   * @returns Array of column counts per block (e.g. [4, 3, 4, 6])
   */
  const getBlockSizes = useCallback((): number[] => {
    const sizes: number[] = [];
    // All sections share the same block structure — use first section only
    const firstSection = Object.values(pageLayout?.pageSections || {})[0];
    if (firstSection) {
      forEach(firstSection.blocks, (block) => {
        sizes.push(Object.keys(block).length);
      });
    }
    return sizes;
  }, [pageLayout]);

  // Standard loader for Page and Deckline Driver View
  if (isDataLoading || !pageDeckColumns || (!isEmpty(pageDecklines) && isEmpty(pageRowData))) {
    return <AurigaLoader loaderText='Loading Page...' />;
  }

  // Render the main grids view once data is no longer loading and is ready
  return (
    <Box className='page-component'>
      <Box className='page-action-controls'>
        <Box className='left-action-controls'>
          {/* Column Visibility Toggles - Dynamic based on available page grains */}
          <Box className='granularity-controls'>
            <ButtonGroup size='medium'>
              {availablePageGrains.map((grain) => (
                <Button
                  key={grain}
                  variant={selectedGranularity === grain ? 'contained' : 'outlined'}
                  onClick={() => setSelectedGranularity(grain)}
                >
                  {grain}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <GFTSearchInput
            className='page-search-input'
            placeholder='Search...'
            minSearchLength={MINIMUM_SEARCH_TERM_LENGTH}
            value={searchTerm}
            loading={isSearching}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />
          {searchMatches.size > 0 && (
            <Box className='page-results-text'>
              <Typography variant='body1'>{`${searchMatches.size} results found.`}</Typography>
            </Box>
          )}
        </Box>
        <Box className='right-action-controls'>
          <Tooltip title='Edit Page'>
            <Box>
              <IconButton
                disabled={pageLockStatus.isEditingDisabled || !currentCorePageName}
                size='medium'
                onClick={() =>
                  navigate(
                    `/deck-preparation/decks/${currentDeckId}/page-setup/${encodeURIComponent(currentCorePageName!)}`,
                  )
                }
              >
                <NoteAltIcon />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title='Download'>
            <IconButton
              size='medium'
              onClick={() => {
                // Enabled Download
                if (featureFlags.enableDownloadPage) {
                  const workbookExportName = `${currentCorePageName}_Export_${new Date().toISOString()}`;
                  // To capture individual output of AGGrid supported 'getSheetDataForExcel' command
                  const sheets: string[] = [];
                  let finalApiReference: GridApi | null = null;

                  gridManagerRef.current?.executeOnAll((gridApi, index, gridCount) => {
                    // Filter out dimension* column additions in export
                    const columnsToExport = gridApi
                      .getColumns()
                      ?.filter((col) => {
                        return !col.getColId().startsWith('dimension-');
                      })
                      .map((col) => col.getColId());

                    // Number of columns to merge across for headers (excludes row number col, KPI name col, and the header cell itself)
                    const mergeAcrossColumns = (columnsToExport?.length || 3) - 3;

                    const sheetData = gridApi.getSheetDataForExcel({
                      sheetName: `${currentCorePageName}-${gridApi.getGridId()}`,
                      columnKeys: columnsToExport,
                      prependContent: [
                        {
                          cells: [], // Add a blank row to exported data to match Executive Deck
                        },
                        {
                          cells: [
                            {
                              // Granularity label only appears on the first section header
                              data: {
                                value: index === 0 ? `${selectedGranularity}` : '',
                                type: 'String',
                              },
                              mergeAcross: 1, // Merges 2 columns
                              styleId: 'excel-report-granularity',
                            },
                            {
                              // Report header only appears on the first section
                              data: {
                                value: index === 0 ? `${pageDeckColumns?.[0]?.report_header}` : '',
                                type: 'String',
                              },
                              mergeAcross: mergeAcrossColumns,
                              styleId: 'excel-report-header',
                            },
                          ],
                        },
                        {
                          cells: [
                            {
                              data: {
                                value: '',
                                type: 'String',
                              },
                            },
                            {
                              data: {
                                value: '',
                                type: 'String',
                              },
                            },
                            // TODO: Some post processing outside of AGGrid will need to occur, as AGGrid excel export does not have a 'mergeDown' to merge cells vertically
                            {
                              // Monetary unit only appears on the first section
                              data: {
                                value: index === 0 ? `${defaultMonetaryUnit}` : '',
                                type: 'String',
                              },
                              mergeAcross: mergeAcrossColumns,
                              styleId: 'excel-report-monetary-unit',
                            },
                          ],
                        },
                        {
                          // Export column letter identifiers row (A, B, C... matching DeckColumn column_name) - only appears on the first section
                          cells: [
                            { data: { value: '', type: 'String' } },
                            { data: { value: '', type: 'String' } },
                            ...(index === 0
                              ? (pageDeckColumns || [])
                                  .filter(
                                    (col, idx, arr) => arr.findIndex((c) => c.column_no === col.column_no) === idx,
                                  )
                                  .sort((a, b) => (a.column_no || 0) - (b.column_no || 0))
                                  .map((col) => ({
                                    data: { value: col.column_name || '', type: 'String' as const },
                                    styleId: 'excel-column-name',
                                  }))
                              : []),
                          ],
                        },
                      ],
                      // Footnotes only appended on the last section grid (appears at bottom of merged sheet)
                      // Footnotes start at column B (1 col earlier than headers at C), so +1 to reach same end column
                      appendContent:
                        index === gridCount - 1 ? getFormattedFootnotesForExport(mergeAcrossColumns + 1) : [],
                      processGroupHeaderCallback: (params) => {
                        // Return groupHeader minus any carriage or newline returns
                        const groupHeaderName = params.api.getDisplayNameForColumnGroup(params.columnGroup, null);

                        // Remove the default Report name being exported, since it ie being replaced with custom prepended text
                        if (
                          groupHeaderName ===
                          `${pageDeckColumns?.[0]?.report_header} (${selectedGranularity})\n${defaultMonetaryUnit}`
                        )
                          return '';

                        return groupHeaderName.replace(/[\r\n]+/g, ' ').trim();
                      },
                      processHeaderCallback: (params) => {
                        // Note*: This assumes the final row above the data grid will be a time series. Should this change,
                        // will need to revisit and look to processGroupHeaderCallback for modifications of the time series headers
                        const headerName = params.api.getDisplayNameForColumn(params.column, null);
                        if (selectedGranularity === PageGranularity.MTD) {
                          return headerName.trim().replace(' ', '-');
                        }

                        return headerName;
                      },
                      processCellCallback: (params) => {
                        // Special handling for the KPI Name column, to format the KPI Name with Footnote Superscript
                        const { column, node, value: kpiNameDefault } = params;
                        if (column.getColId() === 'kpi_name' && node?.data) {
                          const { rawDecklineData } = node.data;
                          const {
                            actual_kpi_name: actualKpiName,
                            footnotes,
                            kpi_name_indent_level: indentLevel,
                          } = rawDecklineData;

                          const indentPrefix = indentLevel ? ' '.repeat(indentLevel * KPI_NAME_INDENT_SPACES) : '';
                          const baseName = actualKpiName || kpiNameDefault;

                          if (!footnotes?.length) return `${indentPrefix}${baseName}`;

                          const formattedFootnotes = (footnotes as number[])
                            .map((footnoteNo: number) => {
                              return convertFootnoteNumberToSuperscript(footnoteNo.toString());
                            })
                            .join('\u02D2'); // Unicode raised comma match

                          return `${indentPrefix}${baseName}${formattedFootnotes}`;
                        }

                        return kpiNameDefault;
                      },
                    });

                    // Add section sheet data to collection
                    if (sheetData) sheets.push(sheetData);

                    // Track latest reference to a GridApi to be able to export sheets as a workbook
                    finalApiReference = gridApi;
                  });

                  // Export the final Excel workbook via ExcelJS (merges sheets vertically + auto-fits columns)
                  if (!isNil(finalApiReference)) {
                    const gridApi = finalApiReference as GridApi;
                    const blob = gridApi.getMultipleSheetsAsExcel({ data: sheets });
                    if (blob) {
                      // Build row-level format array for numFmt application (excludes blank lines)
                      const rowCellFormats = pageDecklines
                        .filter((dl) => determineDecklineType(dl) !== PageSidePanelDropDown.BLANK_LINE)
                        .map((dl) => dl.format_cell_default ?? null);

                      processExcelExport(blob, currentCorePageName || 'Export', getBlockSizes(), rowCellFormats)
                        .then((processedBlob) => {
                          downloadBlob(processedBlob, `${workbookExportName}.xlsx`);
                          toastNotification({
                            visible: true,
                            message: 'Excel export generated. Check downloads folder.',
                            type: 'info',
                          });
                        })
                        .catch((error) => {
                          console.error('[Export] Excel post-processing failed:', error);
                          toastNotification({
                            visible: true,
                            message: 'Excel export failed. Please try again.',
                            type: 'error',
                          });
                        });
                    }
                  }
                } else {
                  // Disabled Download
                  console.debug('TODO Download...');
                }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {featureFlags.enableAuditHistory && (
            <Button
              className='audit-history-button'
              variant='outlined'
              size='medium'
              startIcon={<AccessTimeIcon />}
              onClick={() => setSearchParams({ history: 'page' })}
            >
              Page History
            </Button>
          )}
          {pageLockStatus.isLocked && pageLockStatus.isLockedByCurrentUser && (
            <AsyncSaveButton
              inProgress={isSaving}
              inProgressContent='Please Wait...'
              buttonProps={{
                className: 'action-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: isSaving || pageLockStatus.isEditingDisabled,
                onClick: handleSaveChanges,
              }}
            >
              <Typography variant='inherit'>Save Changes</Typography>
            </AsyncSaveButton>
          )}
          {/* Lock/Release Page Button */}
          <Box className='lock-deck-section'>
            <LockPageButton
              deckId={currentDeckId ?? undefined}
              pageName={currentCorePageName ?? undefined}
              currentPage={currentGranularityPage}
            />
          </Box>
        </Box>
      </Box>
      <Box
        className='grid-layout'
        ref={gridLayoutCallbackRef}
        // --max-grid-height is only applied to single-section pages; multi-section pages fall back to 80vh default
        style={
          {
            '--footnotes-section-height': `${footnotesSectionHeight}px`,
            ...(maxGridHeight !== null &&
              Object.keys(pageLayout?.pageSections || {}).length <= 1 && { '--max-grid-height': `${maxGridHeight}px` }),
          } as CSSProperties
        }
      >
        {/* Render Page and Deckline Driver View Grid(s) */}
        {pageDeckColumns && pageDeckColumns.length > 0 && renderPageGrids()}
        {(!pageDeckColumns || pageDeckColumns.length === 0) && (
          <Typography variant='h6' className='no-data-message'>
            No deck data available
          </Typography>
        )}
      </Box>
      {/* Footnotes Section */}
      {featureFlags.enableFootnotes && !isDataLoading && (
        <FootnotesSection ref={footnotesRef} containerWidth={footnotesContainerWidth} />
      )}
      {featureFlags.enableFootnotes && <FootnotesModal isEditingDisabled={pageLockStatus.isEditingDisabled} />}
      {/* Comments Drawer */}
      <CommentsDrawer {...commentsDrawer} isEditingDisabled={pageLockStatus.isEditingDisabled} />
      {/* Display Name Override Dialog */}
      <DisplayNameDialog {...displayNameDialog} />
      {/* Audit History Dialog (Page and Deckline) */}
      {featureFlags.enableAuditHistory && <AuditHistoryDialog />}
      {/* Saving progress dialog */}
      {/* Dialog is fully controlled by isSaveInProgress — no user dismissal allowed */}
      <Dialog className='saving-dialog' open={isSaveInProgress}>
        <DialogContent>
          <AurigaLoader loaderText='Saving changes...' loaderSize={75} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Page;
